import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LinearProgress } from '@mui/material';

import { Container, ErrorMessage, Loading } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { ExamInformationDTO, DisplayQuestionDTO } from './models/QuestionListDTOs';
import { type QtiQuestionDTO, type QtiStimulusQuestionDTO, QuestionRenderer } from '@/src/lib';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const QuestionListPage: React.FC = () => {
    document.title = 'Question List';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [examData, setExamData] = useState<ExamInformationDTO | null>();
    const [questionData, setQuestionData] = useState<DisplayQuestionDTO[] | null>(null);
    const [nQuestionsLoaded, setNQuestionsLoaded] = useState(0);
    const [nQuestionsTotal, setNQuestionsTotal] = useState(0);

    const { examId = '0' } = useParams();

    useEffect(() => {
        (async function init() {
            try {
                const examRes = await apiRequest<ExamInformationDTO>(
                    'get',
                    `reports/questionList/list/${examId}`
                );
                const examResData = examRes.data;
                setExamData(examResData);

                const questionIds = examResData.questions;
                setNQuestionsTotal(questionIds.length);

                const newQuestionData: DisplayQuestionDTO[] = [];
                let currentQuestionIndex = 1;

                let nLoaded = 0;
                for (const qId of questionIds) {
                    const questionRes = await apiRequest<QtiQuestionDTO>(
                        'get',
                        `assessments/questions/${qId}`
                    );
                    const question = questionRes.data;

                    let displayValue = '';

                    if (question.meta.questionTypeMasterId === qtm.STIMULUS) {
                        const stimQuestion = question as QtiStimulusQuestionDTO;
                        displayValue = `STIM Q${currentQuestionIndex}-${currentQuestionIndex + (stimQuestion.selectedQuestions?.length || 0) - 1}`;
                    } else {
                        displayValue = `Q${currentQuestionIndex}`;
                        currentQuestionIndex++;
                    }

                    newQuestionData.push({
                        displayValue,
                        question: questionRes.data,
                    });

                    nLoaded += 1;
                    setNQuestionsLoaded(nLoaded);
                }
                setQuestionData(newQuestionData);
            } catch {
                setError('Failed to load assessment data.');
            } finally {
                setLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="text-xl font-bold mb-4">{examData?.title}</h1>

            <br />

            <Loading visible={loading && !examData} message="Loading assessment data..." />

            {loading && examData && !questionData && (
                <>
                    <div className="mb-2">
                        <LinearProgress
                            variant="determinate"
                            value={(nQuestionsLoaded * 100) / (nQuestionsTotal || 100)}
                        />
                    </div>
                    Loading questions ({nQuestionsLoaded} / {nQuestionsTotal})...
                </>
            )}
            <ErrorMessage error={error} />

            <br />

            <div>
                {questionData?.map((q) => {
                    return (
                        <div
                            className="flex flex-row items-start gap-3 mb-4"
                            key={`questionList-${q.question.id}`}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    borderRadius: '6px',
                                    padding: '6px 14px',
                                    background: '#d5ecff',
                                    fontSize: '1rem',
                                    minWidth: '44px',
                                    overflow: 'hidden',
                                    flex: '0 0 auto',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {q.displayValue}
                            </div>

                            <div className="flex-1 min-w-0">
                                <QuestionRenderer
                                    question={q.question}
                                    loadedUserResponse={q.question.correctAnswer}
                                    setQuestionLoading={() => {}}
                                    stimMode="sev"
                                    options={{
                                        language: 'en',
                                        disabled: true,
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </Container>
    );
};

export default QuestionListPage;
